<template>
    <div class="px-2 py-4 xl:px-10">
        <div class="max-w-8xl mx-auto w-full space-y-4">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Teachers Management
                </h1>
                <div class="flex space-x-3">
                    <button
                        @click="openImportModal"
                        class="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                        Import Teacher
                    </button>
                    <button
                        @click="openModal"
                        class="inline-flex items-center px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                    >
                        <Plus class="w-4 h-4 mr-1" />
                        Add Teacher
                    </button>
                </div>
            </div>

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <!-- Search -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Search
                        </label>
                        <input
                            v-model="searchQuery"
                            @input="debouncedSearch"
                            type="text"
                            placeholder="Search by name, NIP, or specialization..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <!-- Specialization -->
                    <div>
                        <SearchableSelect
                            v-model="filters.specialization"
                            :options="specializationOptions"
                            label="Specialization"
                            placeholder="All Specializations"
                            option-label="label"
                            option-value="value"
                            @update:model-value="fetchTeachers"
                        />
                    </div>

                    <!-- Status -->
                    <div>
                        <SearchableSelect
                            v-model="filters.is_homeroom"
                            :options="statusOptions"
                            label="Status"
                            placeholder="All Teachers"
                            option-label="label"
                            option-value="value"
                            @update:model-value="fetchTeachers"
                        />
                    </div>

                    <PageLengthSelector
                        v-model="teacherStore.perPage"
                        @update:model-value="handlePerPageChange"
                    />
                </div>
                <div class="flex justify-between items-center mt-4">
                    <div></div>
                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <RotateCcw class="w-4 h-4 mr-1" />
                        Reset Filters
                    </button>
                </div>
            </div>

            <!-- Loading State -->
            <div
                v-if="teacherStore.loading"
                class="flex justify-center items-center py-12"
            >
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                ></div>
            </div>

            <!-- Error State -->
            <div
                v-if="teacherStore.error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <div class="h-5 w-5 text-red-400">⚠️</div>
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-red-800 dark:text-red-200"
                        >
                            Error
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                            {{ teacherStore.error }}
                        </p>
                        <button
                            @click="teacherStore.clearError"
                            class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>

            <!-- Data Table -->
            <div
                v-if="!teacherStore.loading && !teacherStore.error"
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <div
                    class="overflow-x-auto bg-gray-800 dark:bg-gray-900 shadow-md rounded-lg"
                >
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Name
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Teacher ID
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    NIP
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Specialization
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Status
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="teacher in teacherStore.teachers"
                                :key="teacher.id"
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                        <div class="m-0">
                                            <div
                                                class="text-sm font-medium text-gray-900 dark:text-white"
                                            >
                                                {{
                                                    teacher.user?.name ||
                                                    "Unknown"
                                                }}
                                            </div>
                                            <div
                                                class="text-sm text-gray-500 dark:text-gray-400"
                                            >
                                                {{
                                                    teacher.user?.email ||
                                                    "No email"
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ teacher.teacher_id }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ teacher.nip }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ teacher.specialization || "-" }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        v-if="teacher.is_homeroom"
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                    >
                                        Homeroom
                                    </span>
                                    <span
                                        v-else
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                                    >
                                        Regular
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                                >
                                    <button
                                        @click="editTeacher(teacher)"
                                        title="Edit Teacher"
                                        class="inline-flex items-center justify-center p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors mr-2"
                                    >
                                        <Pencil class="w-4 h-4" />
                                    </button>
                                    <button
                                        @click="confirmDelete(teacher)"
                                        title="Delete Teacher"
                                        class="inline-flex items-center justify-center p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <Trash2 class="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty State -->
                <div
                    v-if="teacherStore.teachers.length === 0"
                    class="text-center py-12"
                >
                    <Users class="mx-auto h-12 w-12 text-gray-400" />
                    <h3
                        class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                        No teachers found
                    </h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Get started by adding a new teacher.
                    </p>
                    <div class="mt-6">
                        <button
                            @click="openModal"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <Plus class="w-4 h-4 mr-1" />
                            Add Teacher
                        </button>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div
                v-if="
                    (teacherStore.meta?.total || 0) >
                    (teacherStore.meta?.per_page || 10)
                "
                class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6"
            >
                <div class="flex-1 flex justify-between sm:hidden">
                    <button
                        @click="previousPage"
                        :disabled="teacherStore.meta?.current_page === 1"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Previous
                    </button>
                    <button
                        @click="nextPage"
                        :disabled="
                            teacherStore.meta?.current_page ===
                            teacherStore.meta?.last_page
                        "
                        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Next
                    </button>
                </div>
                <div
                    class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
                >
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Showing
                            <span class="font-medium">{{
                                teacherStore.meta?.from ?? 0
                            }}</span>
                            to
                            <span class="font-medium">{{
                                teacherStore.meta?.to ?? 0
                            }}</span>
                            of
                            <span class="font-medium">{{
                                teacherStore.meta?.total ?? 0
                            }}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <nav
                            class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                            aria-label="Pagination"
                        >
                            <button
                                @click="previousPage"
                                :disabled="
                                    teacherStore.meta?.current_page === 1
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Previous
                            </button>
                            <button
                                v-for="page in visiblePages"
                                :key="page"
                                @click="
                                    typeof page === 'number'
                                        ? goToPage(page)
                                        : null
                                "
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    page === teacherStore.meta?.current_page
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300',
                                    typeof page === 'string'
                                        ? 'cursor-not-allowed'
                                        : 'cursor-pointer',
                                ]"
                                :disabled="typeof page === 'string'"
                            >
                                {{ page }}
                            </button>
                            <button
                                @click="nextPage"
                                :disabled="
                                    teacherStore.meta?.current_page ===
                                    teacherStore.meta?.last_page
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- Teacher Modal -->
            <TeacherModal
                v-if="showModal"
                :teacher="selectedTeacher"
                :is-editing="isEditing"
                @close="closeModal"
                @saved="handleSaved"
            />

            <!-- Delete Confirmation Modal -->
            <DeleteConfirmationModal
                v-if="showDeleteModal"
                :show="showDeleteModal"
                :title="deleteModalTitle"
                :message="deleteModalMessage"
                @confirm="handleDelete"
                @close="closeDeleteModal"
            />

            <!-- Import Teacher Modal -->
            <ImportTeacherModal
                v-if="showImportModal"
                @close="closeImportModal"
                @success="handleImportSuccess"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useTeacherStore } from "@/stores/teacherStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import TeacherModal from "@/components/TeacherModal.vue";
import ImportTeacherModal from "@/components/ImportTeacherModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import PageLengthSelector from "@/components/PageLengthSelector.vue";
import NotificationService from "@/utils/notifications";
import type { Teacher } from "@/api/teacher";
import { Plus, RotateCcw, Pencil, Trash2, Users } from "@lucide/vue";

const teacherStore = useTeacherStore();
// State
const searchQuery = ref("");
const filters = ref({
    specialization: "",
    is_homeroom: undefined as boolean | undefined,
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const showImportModal = ref(false);
const selectedTeacher = ref<Teacher | null>(null);
const isEditing = ref(false);
const teacherToDelete = ref<Teacher | null>(null);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this teacher? This action cannot be undone.",
);

// Computed
const specializations = computed(() => {
    const specs = new Set(
        teacherStore.teachers
            .map((t: Teacher) => t.specialization)
            .filter(Boolean),
    );
    return Array.from(specs).sort();
});

// Options for Specialization filter
const specializationOptions = computed(() => {
    const options = [{ value: "", label: "All Specializations" }];
    specializations.value.forEach((spec: string | undefined) => {
        if (spec) {
            options.push({ value: spec, label: spec });
        }
    });
    return options;
});

// Options for Status filter
const statusOptions = computed(() => [
    { value: undefined, label: "All Teachers" },
    { value: true, label: "Homeroom Teachers" },
    { value: false, label: "Regular Teachers" },
]);

const visiblePages = computed(() => {
    const current = teacherStore.meta?.current_page || 1;
    const last = teacherStore.meta?.last_page || 1;
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
        let i = Math.max(2, current - delta);
        i <= Math.min(last - 1, current + delta);
        i++
    ) {
        range.push(i);
    }

    if (current - delta > 2) {
        rangeWithDots.push(1, "...");
    } else {
        rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (current + delta < last - 1) {
        rangeWithDots.push("...", last);
    } else if (last > 1) {
        rangeWithDots.push(last);
    }

    return rangeWithDots;
});

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.specialization !== "" ||
        filters.value.is_homeroom !== undefined ||
        teacherStore.page !== 1 ||
        teacherStore.perPage !== 10
    );
});

// Methods
const handlePerPageChange = (value: number) => {
    teacherStore.perPage = value;
    teacherStore.page = 1;
    fetchTeachers();
};

const fetchTeachers = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await teacherStore.fetchTeachers(searchFilters);
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value = { specialization: "", is_homeroom: undefined };
    teacherStore.setPage(1);
    teacherStore.setPerPage(10);
    fetchTeachers();
};

const debouncedSearch = useDebounce(fetchTeachers, 300);

const openModal = () => {
    selectedTeacher.value = null;
    isEditing.value = false;
    showModal.value = true;
};

const editTeacher = (teacher: Teacher) => {
    selectedTeacher.value = teacher;
    isEditing.value = true;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    selectedTeacher.value = null;
    isEditing.value = false;
};

const confirmDelete = (teacher: Teacher) => {
    teacherToDelete.value = teacher;
    deleteModalTitle.value = "Delete Teacher";
    deleteModalMessage.value = `Are you sure you want to delete ${teacher.user?.name || "this teacher"}? This action cannot be undone.`;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    teacherToDelete.value = null;
};

const handleSaved = () => {
    // Refresh the teachers list to show the new/updated teacher
    fetchTeachers();
    showNotification(
        isEditing.value
            ? "Teacher updated successfully"
            : "Teacher created successfully",
        "success",
    );
    closeModal();
};

const handleDelete = async () => {
    try {
        if (teacherToDelete.value) {
            await teacherStore.deleteTeacher(teacherToDelete.value.id);
            showNotification("Teacher deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete teacher", "error");
    }
};

const openImportModal = () => {
    showImportModal.value = true;
};

const closeImportModal = () => {
    showImportModal.value = false;
};

const handleImportSuccess = () => {
    showNotification("Teachers imported successfully", "success");
    fetchTeachers();
};

const previousPage = () => {
    if ((teacherStore.meta?.current_page || 1) > 1) {
        teacherStore.setPage((teacherStore.meta?.current_page || 1) - 1);
        fetchTeachers();
    }
};

const nextPage = () => {
    if (
        (teacherStore.meta?.current_page || 1) <
        (teacherStore.meta?.last_page || 1)
    ) {
        teacherStore.setPage((teacherStore.meta?.current_page || 1) + 1);
        fetchTeachers();
    }
};

const goToPage = (page: number) => {
    teacherStore.setPage(page);
    fetchTeachers();
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchTeachers();
});
</script>
